import { useContext } from 'react';
import { useRoutes } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Provider as ReduxProvider } from 'react-redux';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import 'src/global.css';
import 'src/locales/i18n';

import { SplashScreen } from 'src/components/splash-screen';
import { Toaster } from 'src/components/toaster';
import { AuthConsumer, AuthProvider } from 'src/contexts/auth/jwt';
import { SettingsContext, SettingsProvider } from 'src/contexts/settings';
import { gtmConfig } from 'src/config';
import { useNprogress } from 'src/hooks/use-nprogress';
import { useAnalytics } from 'src/hooks/use-analytics';
import { routes } from 'src/routes';
import { store } from 'src/store';
import { createTheme } from 'src/theme';

const AppContent = () => {
  const { colorPreset, paletteMode } = useContext(SettingsContext);

  const theme = createTheme({
    colorPreset,
    contrast: 'normal',
    direction: 'ltr',
    paletteMode,
    responsiveFontSizes: true,
  });

  const element = useRoutes(routes);

  return (
    <ThemeProvider theme={theme}>
      <Helmet>
        <meta
          name="color-scheme"
          content={paletteMode}
        />
        <meta
          name="theme-color"
          content={theme.palette.neutral[900]}
        />
      </Helmet>
      <CssBaseline />
      {element}
      <Toaster reverseOrder={false} />
    </ThemeProvider>
  );
};

export const App = () => {
  useAnalytics(gtmConfig);
  useNprogress();

  return (
    <ReduxProvider store={store}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <AuthProvider>
          <AuthConsumer>
            {(auth) => {
              const showSlashScreen = !auth.isInitialized;

              if (showSlashScreen) {
                return <SplashScreen />;
              }

              return (
                <SettingsProvider>
                  <AppContent />
                </SettingsProvider>
              );
            }}
          </AuthConsumer>
        </AuthProvider>
      </LocalizationProvider>
    </ReduxProvider>
  );
};
